import {
  InputAdornment,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
  SxProps,
  Theme,
} from "@mui/material";
import React, { useMemo } from "react";
import useConditionalTranslation from "../../utils/hooks/useConditionalTranslation";

export interface TextFieldProps extends Omit<MuiTextFieldProps, "variant"> {
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  dataTestId?: string;
  label?: string;
  placeholder?: string;
  errorText?: React.ReactNode;
  AccessoryStart?: React.ReactNode;
  AccessoryEnd?: React.ReactNode;
  maxLength?: number;
  required?: boolean;
  containerStyle?: React.CSSProperties;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  readOnly?: boolean;
}

const TextField: React.FC<TextFieldProps> = ({
  dataTestId,
  label,
  errorText,
  error,
  placeholder,
  helperText,
  AccessoryStart,
  AccessoryEnd,
  maxLength,
  containerStyle = null,
  fullWidth = true,
  required = false,
  onChange,
  onClick,
  sx = {},
  readOnly = false,
  disabled = false,
  ...props
}) => {
  if (errorText) {
    error = true;
    helperText = errorText;
  }
  const [tLabel, tPlaceholder, tHelperText] = useConditionalTranslation([
    label,
    placeholder,
    helperText?.toString(),
  ]);
  const normalizedValue = props.value === null ? "" : props.value;

  const name = tLabel ? `${tLabel}${required ? " *" : ""}` : undefined;

  const InputProps = useMemo(() => {
    let inputProps: Partial<TextFieldProps["InputProps"]> = {};
    if (dataTestId) {
      inputProps = {
        // @ts-expect-error
        "data-testid": dataTestId,
        ...inputProps,
      };
    }
    if (maxLength) {
      inputProps = {
        // @ts-expect-error
        maxLength,
        ...inputProps,
      };
    }
    if (AccessoryStart) {
      if (props.InputProps?.startAdornment) {
        inputProps.startAdornment = (
          <>
            <InputAdornment position="start">{AccessoryStart}</InputAdornment>
            {props?.inputProps?.startAdornment}
          </>
        );
      } else {
        inputProps.startAdornment = (
          <InputAdornment position="start">{AccessoryStart}</InputAdornment>
        );
      }
    }
    if (AccessoryEnd) {
      inputProps.endAdornment = (
        <InputAdornment position="end">{AccessoryEnd}</InputAdornment>
      );
    }
    return inputProps;
  }, [AccessoryStart, AccessoryEnd, props, dataTestId, maxLength]);

  return (
    <MuiTextField
      id={dataTestId}
      variant="outlined"
      size="small"
      {...props}
      value={normalizedValue}
      label={name}
      error={error}
      onChange={onChange}
      placeholder={tPlaceholder}
      helperText={tHelperText}
      fullWidth={fullWidth}
      InputProps={InputProps}
      disabled={disabled}
      InputLabelProps={{ shrink: true, ...(props?.InputLabelProps || {}) }}
      style={containerStyle}
      sx={sx}
      onClick={onClick}
    />
  );
};

export default TextField;
